import { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, CheckCircle, ChevronLeft, ChevronRight,
  Box, Palette, Printer, Package, Globe, Gift, Image, TrendingUp,
  Phone, Mail, Clock, Zap, Award, Star,
} from "lucide-react";
import "./ServiceDetail.css";

/* ─── asset imports ─────────────────────────────────────────── */
import largeFormatImg from "../assets/images/largeFormat.jpeg";
import largeFormat    from "../assets/images/largeFormatVid.mov";

import bags1      from "../assets/images/bags1.jpg";
import bags2      from "../assets/images/bags2.jpeg";
import boxes      from "../assets/images/boxes.jpeg";

import threeD1    from "../assets/images/threeD2.jpeg";
import threeD2    from "../assets/images/threeD.jpg";
import threeD3    from "../assets/images/threeD1.jpg";

import brand1     from "../assets/images/brand1.jpeg";
import brand2     from "../assets/images/brand2.jpeg";
import brand3     from "../assets/images/brand3.jpeg";
import brand4     from "../assets/images/brand4.jpg";
import brand5     from "../assets/images/brand5.jpg";

import brochure   from "../assets/images/brochure.jpg";
import flyers     from "../assets/images/flyers.jpg";
import gift1      from "../assets/images/gift1.jpg";
import bdCard     from "../assets/images/bdCard.jpg";

import tshirt     from "../assets/images/tshirt.jpg";
import tshirt2    from "../assets/images/tshirtsPrinting.jpg";
import wristband  from "../assets/images/wristband.jpg";
import reflectors from "../assets/images/reflectors.jpg";
import lanyard    from "../assets/images/lanyard.jpg";
import caps       from "../assets/images/Caps.jpg";

import book1      from "../assets/images/book1.jpeg";
import book2      from "../assets/images/book2.jpeg";
import book3      from "../assets/images/book3.jpeg";
import book4      from "../assets/images/book4.jpg";

/* ─── service data ───────────────────────────────────────────── */
const SERVICES = [
  {
    id: "3d-signage",
    Icon: Box,
    label: "3D Signage",
    cat: "Signage",
    tag: "Most Popular",
    heroTagline: "Be the first thing people see — and the last they forget.",
    description:
      "Your physical presence speaks before your team does. We fabricate, finish and install premium 3D signage that turns storefronts, lobbies and boardrooms into brand statements. From channel letters to backlit reception walls, every piece is engineered for impact and longevity in Kenya's climate.",
    images: [threeD3, threeD2, threeD1],
    features: [
      { title: "Shop & Fascia Signs",        desc: "Externally mounted lettering and logo panels that define your shopfront from across the street." },
      { title: "Illuminated Channel Letters", desc: "LED-lit acrylic or stainless steel letters — vivid by day, luminous at night." },
      { title: "Reception & Lobby Signs",    desc: "Dimensional logo walls that make the right first impression on every client who walks through the door." },
      { title: "Wayfinding Systems",         desc: "Floor-standing pylons, directional arrows and room identifiers that guide visitors with confidence." },
      { title: "3D Logo Standoffs",          desc: "Raised metal or acrylic logos on standoff hardware for a shadow-cast effect on feature walls." },
      { title: "Vehicle Lettering",          desc: "Precision-cut vinyl and dimensional lettering applied to fleet vehicles for mobile brand coverage." },
    ],
    process: [
      { step: "Consult",   desc: "We visit your site, assess the space and understand your brand guidelines." },
      { step: "Design",    desc: "Technical drawings and 3D renders produced for your approval before anything is fabricated." },
      { step: "Fabricate", desc: "Built in our workshop using laser-cut acrylic, aluminium composite and LED modules." },
      { step: "Install",   desc: "Professional site installation with clean cable management and structural fixings." },
    ],
    why: [
      "Weatherproof materials rated for Kenya's climate",
      "Lifetime LED warranty on all illuminated units",
      "Site survey included at no extra charge",
      "Installed by our own certified team — no sub-contractors",
    ],
    turnaround: "7–14 working days",
    startingFrom: "KES 15,000",
  },
  {
    id: "branding",
    Icon: Palette,
    label: "Brand Identity",
    cat: "Branding",
    heroTagline: "A brand is a promise. We make sure yours is one worth keeping.",
    description:
      "Great branding is the difference between being chosen and being passed over. We build visual identities from the ground up — logo, colour palette, typography, brand voice and full usage guidelines — so your business looks coherent, credible and compelling everywhere it shows up.",
    images: [brand1, brand2, brand3, brand4, brand5],
    features: [
      { title: "Logo Design",       desc: "Bespoke marks crafted to be distinctive, scalable and timeless — not trend-chasing." },
      { title: "Brand Guidelines",  desc: "A comprehensive rulebook covering logo usage, colour codes, typefaces and tone of voice." },
      { title: "Stationery Suite",  desc: "Business cards, letterheads, envelopes and email signatures that unify every touchpoint." },
      { title: "Social Media Kits", desc: "Templated post layouts, story formats and profile assets sized for every platform." },
      { title: "Brand Messaging",   desc: "Tagline, value proposition and copy tone developed alongside the visual identity." },
      { title: "Packaging Design",  desc: "Label and packaging artwork that communicates your brand on shelf and at the point of sale." },
    ],
    process: [
      { step: "Discovery", desc: "Brand questionnaire, competitor audit and positioning workshop to understand where you sit in the market." },
      { step: "Concepts",  desc: "Three distinct logo directions presented with rationale — we show the thinking, not just the pixels." },
      { step: "Refine",    desc: "Two rounds of revisions on the chosen direction, with full colour and application mockups." },
      { step: "Deliver",   desc: "Final files in every format (AI, EPS, SVG, PNG, PDF) plus a printed brand booklet." },
    ],
    why: [
      "All files delivered in print and digital formats",
      "Includes colour and monochrome variants",
      "Usage guidelines written in plain language",
      "Unlimited file re-exports for the life of the project",
    ],
    turnaround: "10–21 working days",
    startingFrom: "KES 25,000",
  },
  {
    id: "printing",
    Icon: Printer,
    label: "Printing Services",
    cat: "Print",
    heroTagline: "Print that people pick up, read and keep.",
    description:
      "In a world of screens, well-crafted print commands attention. We operate high-resolution digital and offset presses to produce business cards, brochures, flyers, posters and promotional print that represent your brand at the highest quality — from a single copy to a full print run.",
    images: [brochure, flyers, gift1, bdCard],
    features: [
      { title: "Business Cards",      desc: "Spot-UV, foil, embossed or standard — printed on stocks up to 700gsm for a card that gets kept." },
      { title: "Flyers & Leaflets",   desc: "Single and double-sided, A6 to A3, with full-bleed colour and fast turnaround." },
      { title: "Brochures",           desc: "Saddle-stitched or perfect-bound, up to full A4, with vibrant CMYK print and matte/gloss laminate." },
      { title: "Posters & Banners",   desc: "Indoor display posters and large-format banner prints for events and promotions." },
      { title: "Catalogues",          desc: "Multi-page product or services catalogues with perfect binding and full-colour inside pages." },
      { title: "Branded Stationery",  desc: "Letterheads, compliment slips, notepads and envelopes with consistent brand application." },
    ],
    process: [
      { step: "Artwork Check", desc: "We review your files for bleed, resolution and colour mode before going to press." },
      { step: "Press Proof",   desc: "A digital or physical proof is approved before the full run is printed." },
      { step: "Print",         desc: "Produced on calibrated presses for consistent colour across the entire run." },
      { step: "Finishing",     desc: "Lamination, trimming, folding and binding completed in-house." },
    ],
    why: [
      "CMYK and Pantone colour matching available",
      "Minimum quantities as low as 50 copies",
      "Same-day printing available for urgent jobs",
      "Delivery across Nairobi and nationwide courier",
    ],
    turnaround: "2–5 working days",
    startingFrom: "KES 2,500",
  },
  {
    id: "packaging",
    Icon: Package,
    label: "Packaging Solutions",
    cat: "Packaging",
    heroTagline: "The unboxing moment is a brand moment. Make it count.",
    description:
      "Packaging is the last piece of your brand your customer touches before your product. We design and produce bespoke paper bags, gift boxes, product packaging and custom labels that elevate the perceived value of everything you sell — because presentation is part of the product.",
    images: [bags2, bags1, boxes],
    features: [
      { title: "Paper & Gift Bags",    desc: "Twisted-cord and flat-handle bags in kraft, white art or custom printed stock with your branding." },
      { title: "Rigid & Folding Boxes",desc: "Lid-and-base gift boxes and folding cartons in a range of sizes, finishes and print options." },
      { title: "Product Packaging",    desc: "Custom-shaped boxes and pouches designed to protect and present your product perfectly." },
      { title: "Custom Labels",        desc: "Waterproof, foil or standard labels for jars, bottles, sachets and packaging of any shape." },
      { title: "Tissue & Wrapping",    desc: "Branded tissue paper and wrapping solutions for a complete unboxing experience." },
      { title: "Eco Packaging",        desc: "Recycled and biodegradable materials for brands committed to sustainable packaging." },
    ],
    process: [
      { step: "Brief",     desc: "We understand your product dimensions, weight, quantity and brand positioning." },
      { step: "Prototype", desc: "A physical sample is produced and approved before the full production run." },
      { step: "Produce",   desc: "Full run manufactured on schedule with quality checks at each stage." },
      { step: "Deliver",   desc: "Flat-packed or assembled and delivered to your warehouse or store." },
    ],
    why: [
      "Custom sizes and shapes to fit your exact product",
      "Low minimum order quantities for small businesses",
      "Eco-friendly material options available",
      "Structural design included for all box styles",
    ],
    turnaround: "7–14 working days",
    startingFrom: "KES 8,000",
  },
  {
    id: "web-design",
    Icon: Globe,
    label: "Web Design & Dev",
    cat: "Digital",
    tag: "New",
    heroTagline: "A website that works as hard as you do.",
    description:
      "Your website is your best salesperson — available 24 hours a day, seven days a week. We design and build fast, modern websites that look sharp on every device, rank on Google and convert visitors into enquiries, bookings and sales. No bloated templates. Clean code, deliberate design.",
    images: [
      "https://images.unsplash.com/photo-1547658719-da2b51169166?w=900&q=82",
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900&q=82",
      "https://images.unsplash.com/photo-1558655146-d09347e92766?w=900&q=82",
    ],
    features: [
      { title: "Responsive Design",   desc: "Pixel-perfect on every screen from the smallest mobile to a wide desktop monitor." },
      { title: "SEO Optimised",       desc: "Technical SEO foundations built in from day one — semantic HTML, fast load times, correct meta tags." },
      { title: "E-commerce",          desc: "WooCommerce and Shopify stores with payment integration, stock management and order tracking." },
      { title: "CMS Integration",     desc: "WordPress, Webflow or custom CMS so you can update your own content without touching code." },
      { title: "Performance",         desc: "Pages that score 90+ on Google PageSpeed — compressed assets, lazy loading and clean architecture." },
      { title: "Ongoing Maintenance", desc: "Monthly care plans covering security updates, backups, uptime monitoring and content changes." },
    ],
    process: [
      { step: "Strategy", desc: "Sitemap, user journey and content planning before any design begins." },
      { step: "Design",   desc: "High-fidelity Figma mockups of every key page, reviewed and approved before development." },
      { step: "Build",    desc: "Developed on a staging server so you can review the live site before it goes public." },
      { step: "Launch",   desc: "Domain setup, SSL, redirects and a post-launch technical check before we hand over the keys." },
    ],
    why: [
      "All sites mobile-first and WCAG accessibility compliant",
      "Hosting and domain setup included in project cost",
      "Google Analytics and Search Console connected at launch",
      "12-month post-launch support included as standard",
    ],
    turnaround: "3–8 weeks",
    startingFrom: "KES 45,000",
  },
  {
    id: "merchandise",
    Icon: Gift,
    label: "Promotional Merch",
    cat: "Merchandise",
    heroTagline: "Merchandise people actually use — and remember you for.",
    description:
      "Promotional merchandise that sits in a drawer helps no one. We source, brand and supply items your clients and staff will genuinely use — quality mugs, clean t-shirts, durable caps, wristbands, lanyards and corporate gift sets that keep your brand visible long after the event is over.",
    images: [tshirt, tshirt2, wristband, reflectors, lanyard, caps],
    features: [
      { title: "T-Shirts & Apparel",  desc: "Screen-printed or DTF-printed garments in a full size range, from event tees to embroidered staff polos." },
      { title: "Caps & Headwear",     desc: "6-panel caps, bucket hats and beanies with embroidered or printed logo placement." },
      { title: "Wristbands",          desc: "Silicone, fabric and Tyvek wristbands for events, access control and brand campaigns." },
      { title: "Lanyards",            desc: "Full-colour sublimation lanyards with custom clips, safety breaks and card holders." },
      { title: "Reflective Gear",     desc: "Hi-vis vests, jackets and reflectors branded for safety-conscious industries and night events." },
      { title: "Corporate Gift Sets", desc: "Curated sets — pen, notebook, mug and bag — packaged in branded gift boxes for client gifting." },
    ],
    process: [
      { step: "Sourcing",   desc: "We recommend items that suit your budget, audience and intended use." },
      { step: "Artwork",    desc: "Print-ready artwork prepared and approved, with a physical sample for large orders." },
      { step: "Production", desc: "Branded and quality-checked, with each item inspected before packing." },
      { step: "Delivery",   desc: "Bulk delivery to your office, or individual packaging for corporate gift despatch." },
    ],
    why: [
      "No minimum order on most items",
      "Samples available before committing to a full order",
      "Bulk pricing scales from 50 to 5,000+ units",
      "Custom packaging and gift wrapping available",
    ],
    turnaround: "5–10 working days",
    startingFrom: "KES 500 per unit",
  },
  {
    id: "large-format",
    Icon: Image,
    label: "Large Format Print",
    cat: "Large Format",
    heroTagline: "Make a statement at any scale.",
    description:
      "When you need to be seen from a distance, large-format printing delivers. We produce vivid, weather-resistant banners, rollup displays, window graphics, vehicle wraps and hoarding boards that carry your brand with authority — indoors, outdoors and on the move.",
    images: [largeFormatImg],
    video: largeFormat,
    features: [
      { title: "Rollup Banners",   desc: "Retractable banner stands in standard and wide formats for events, conferences and showrooms." },
      { title: "Hoarding Boards",  desc: "Construction site and event perimeter boards in any dimension, installed and dismantled by our team." },
      { title: "Vehicle Branding", desc: "Partial or full vehicle wraps on cars, vans, buses and matatus with cast vinyl for a 5-year life." },
      { title: "Window Graphics",  desc: "Frosted, perforated or full-colour window films for retail, office privacy and brand display." },
      { title: "Floor Graphics",   desc: "Anti-slip laminated floor vinyls for promotional messaging and wayfinding inside spaces." },
      { title: "Event Backdrops",  desc: "Step-and-repeat media walls and stage backdrops for press, social media and event branding." },
    ],
    process: [
      { step: "Measure", desc: "Site visit or client-supplied dimensions used to prepare accurate print specifications." },
      { step: "Design",  desc: "Artwork produced at correct DPI and scale with bleed and trim allowances built in." },
      { step: "Print",   desc: "Wide-format UV or solvent printing on the appropriate media for the application." },
      { step: "Install", desc: "Site installation for fixed graphics; hardware assembly for banner stands and display systems." },
    ],
    why: [
      "UV-resistant inks rated for 3–5 years outdoor use",
      "Available in any custom dimension",
      "Vehicle wrap warranty against lifting and fading",
      "Same-day printing for urgent event banners",
    ],
    turnaround: "2–7 working days",
    startingFrom: "KES 3,500",
  },
  {
    id: "book-printing",
    Icon: TrendingUp,
    label: "Book Printing",
    cat: "Publishing",
    heroTagline: "From manuscript to printed copy — we handle everything in between.",
    description:
      "Whether you are publishing a church hymnal, a school magazine, a company profile or a novel, we manage the entire production process from artwork preparation to bound and delivered copies. Digital and offset print options available for runs from 10 to 10,000+ copies.",
    images: [book1, book2, book3, book4],
    features: [
      { title: "Perfect Binding",   desc: "Glue-bound square spines for books from 48 pages, ideal for novels, manuals and catalogues." },
      { title: "Saddle Stitching",  desc: "Staple-bound booklets and magazines up to 64 pages — fast, affordable and clean." },
      { title: "School Magazines",  desc: "Full-colour annual magazines with custom covers and section layouts." },
      { title: "Church Hymnals",    desc: "Durable perfect-bound hymnals and service books with ribbon markers." },
      { title: "Company Profiles",  desc: "Premium soft or hardcover profiles for tenders, investor packs and client presentations." },
      { title: "Short-Run Digital", desc: "As few as 10 copies with no setup fee — ideal for proofs, limited editions or small organisations." },
    ],
    process: [
      { step: "File Prep",      desc: "We check your manuscript for bleed, margins, font embedding and image resolution." },
      { step: "Cover Design",   desc: "Cover artwork designed or refined to print-ready standard with barcode if required." },
      { step: "Print",          desc: "Interior pages printed in black & white or full colour, digital or offset depending on run size." },
      { step: "Bind & Deliver", desc: "Bound, trimmed, packed and couriered to your door — or collected from our premises." },
    ],
    why: [
      "Typesetting and layout service available if needed",
      "ISBN application assistance for self-publishers",
      "Rush print available for event and launch deadlines",
      "Nationwide courier delivery within Kenya",
    ],
    turnaround: "5–14 working days",
    startingFrom: "KES 1,200 per copy",
  },
];

/* ─── Image ticker ────────────────────────────────────────────── */
function ImageTicker({ images, video }) {
  const vidRef = useRef(null);

  if (video) {
    return (
      <div className="sd-ticker sd-ticker--video">
        <video
          ref={vidRef}
          className="sd-ticker__video"
          src={video}
          poster={images[0]}
          muted loop autoPlay playsInline
        />
        <div className="sd-ticker__scrim" />
      </div>
    );
  }

  const loop = [...images, ...images, ...images, ...images];

  return (
    <div className="sd-ticker">
      <div className="sd-ticker__row">
        <div className="sd-ticker__track sd-ticker__track--l" style={{ "--ticker-dur": "32s" }}>
          {loop.map((src, i) => (
            <div className="sd-ticker__img" key={i}>
              <img src={src} alt="" loading={i < 4 ? "eager" : "lazy"} />
            </div>
          ))}
        </div>
      </div>
      <div className="sd-ticker__row">
        <div className="sd-ticker__track sd-ticker__track--r" style={{ "--ticker-dur": "40s" }}>
          {[...loop.slice(images.length), ...loop.slice(0, images.length)].map((src, i) => (
            <div className="sd-ticker__img" key={i}>
              <img src={src} alt="" loading="lazy" />
            </div>
          ))}
        </div>
      </div>
      <div className="sd-ticker__edge sd-ticker__edge--l" />
      <div className="sd-ticker__edge sd-ticker__edge--r" />
      <div className="sd-ticker__scrim" />
    </div>
  );
}

/* ─── Feature card ────────────────────────────────────────────── */
function FeatureCard({ title, desc, index }) {
  return (
    <div className="sd-feat" style={{ "--delay": `${index * 0.06}s` }}>
      <div className="sd-feat__num">0{index + 1}</div>
      <div className="sd-feat__body">
        <div className="sd-feat__title">{title}</div>
        <p className="sd-feat__desc">{desc}</p>
      </div>
    </div>
  );
}

/* ─── Process step ────────────────────────────────────────────── */
function ProcessStep({ step, desc, index, total }) {
  return (
    <div className="sd-step">
      <div className="sd-step__num">{String(index + 1).padStart(2, "0")}</div>
      <div className="sd-step__line" style={{ visibility: index < total - 1 ? "visible" : "hidden" }} />
      <div className="sd-step__body">
        <div className="sd-step__title">{step}</div>
        <p className="sd-step__desc">{desc}</p>
      </div>
    </div>
  );
}

/* ─── Gallery carousel ────────────────────────────────────────── */
function Gallery({ images }) {
  const [active, setActive] = useState(0);
  const prev = useCallback(() => setActive(i => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setActive(i => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const t = setInterval(next, 3800);
    return () => clearInterval(t);
  }, [next]);

  if (images.length === 1) {
    return (
      <div className="sd-gallery sd-gallery--single">
        <img src={images[0]} alt="" className="sd-gallery__solo" />
      </div>
    );
  }

  return (
    <div className="sd-gallery">
      <div className="sd-gallery__track">
        {images.map((src, i) => (
          <img
            key={i}
            src={src}
            alt=""
            className={`sd-gallery__img${i === active ? " active" : ""}`}
            loading={i === 0 ? "eager" : "lazy"}
          />
        ))}
      </div>
      <button className="sd-gallery__arrow sd-gallery__arrow--l" onClick={prev} aria-label="Previous image">
        <ChevronLeft size={20} />
      </button>
      <button className="sd-gallery__arrow sd-gallery__arrow--r" onClick={next} aria-label="Next image">
        <ChevronRight size={20} />
      </button>
      <div className="sd-gallery__dots">
        {images.map((_, i) => (
          <button
            key={i}
            className={`sd-gallery__dot${i === active ? " active" : ""}`}
            onClick={() => setActive(i)}
            aria-label={`Image ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

/* ─── Sibling nav ─────────────────────────────────────────────── */
function SiblingNav({ current, navigate }) {
  const others = SERVICES.filter(s => s.id !== current).slice(0, 4);
  return (
    <section className="sd-siblings">
      <div className="sd-siblings__inner">
        <div className="sd-siblings__head">Other Services</div>
        <div className="sd-siblings__grid">
          {others.map(s => (
            <button
              key={s.id}
              className="sd-sibling"
              onClick={() => { navigate(`/services/${s.id}`); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            >
              <div className="sd-sibling__icon"><s.Icon size={18} color="#FF2B2B" strokeWidth={1.6} /></div>
              <div className="sd-sibling__label">{s.label}</div>
              <ArrowRight size={14} className="sd-sibling__arrow" />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Page ────────────────────────────────────────────────────── */
export default function ServiceDetails() {
  const { serviceId } = useParams();
  const navigate      = useNavigate();
  const svc           = SERVICES.find(s => s.id === serviceId) ?? SERVICES[0];

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [serviceId]);

  return (
    <div className="sd">

      <header className="sd-hero">
        <ImageTicker images={svc.images} video={svc.video} />
        <div className="sd-hero__content">
          <div className="sd-hero__eyebrow">
            <svc.Icon size={14} color="#FF2B2B" />
            {svc.cat}
          </div>
          <h1 className="sd-hero__h1">{svc.label}</h1>
          <p className="sd-hero__tagline">{svc.heroTagline}</p>
          <div className="sd-hero__meta">
            <div className="sd-hero__meta-item">
              <Clock size={13} color="#FF2B2B" />
              <span>Turnaround: <strong>{svc.turnaround}</strong></span>
            </div>
            <div className="sd-hero__meta-item">
              <Zap size={13} color="#FF2B2B" />
              <span>From <strong>{svc.startingFrom}</strong></span>
            </div>
          </div>
          <div className="sd-hero__ctas">
            <a href="tel:0794437498" className="sd-btn sd-btn--primary">
              Get a Quote <ArrowRight size={15} />
            </a>
            <button className="sd-btn sd-btn--ghost" onClick={() => navigate(-1)}>
              <ArrowLeft size={15} /> Back
            </button>
          </div>
        </div>
      </header>

      <section className="sd-about">
        <div className="sd-about__inner">
          <div className="sd-about__left">
            <div className="sd-label">About This Service</div>
            <h2 className="sd-h2">What We Deliver</h2>
          </div>
          <div className="sd-about__right">
            <p className="sd-about__body">{svc.description}</p>
            <div className="sd-about__stats">
              {[
                { n: "100+", l: "Projects Completed" },
                { n: "98%",  l: "Client Satisfaction" },
                { n: "5+",   l: "Years Experience"    },
              ].map(s => (
                <div className="sd-stat" key={s.l}>
                  <div className="sd-stat__n">{s.n}</div>
                  <div className="sd-stat__l">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="sd-divider" />

      <section className="sd-features">
        <div className="sd-features__inner">
          <div className="sd-label">What's Included</div>
          <h2 className="sd-h2">Everything You Get</h2>
          <div className="sd-features__grid">
            {svc.features.map((f, i) => (
              <FeatureCard key={f.title} {...f} index={i} />
            ))}
          </div>
        </div>
      </section>

      <div className="sd-divider" />

      <section className="sd-gallery-sec">
        <div className="sd-gallery-sec__inner">
          <div className="sd-label">Our Work</div>
          <h2 className="sd-h2">See It in Action</h2>
          <Gallery images={svc.images} />
        </div>
      </section>

      <div className="sd-divider" />

      <section className="sd-process">
        <div className="sd-process__inner">
          <div className="sd-process__head">
            <div className="sd-label">How It Works</div>
            <h2 className="sd-h2">Our Process</h2>
            <p className="sd-process__sub">Clear steps, honest timelines, no surprises.</p>
          </div>
          <div className="sd-process__steps">
            {svc.process.map((p, i) => (
              <ProcessStep key={p.step} {...p} index={i} total={svc.process.length} />
            ))}
          </div>
        </div>
      </section>

      <div className="sd-divider" />

      <section className="sd-why">
        <div className="sd-why__inner">
          <div className="sd-why__left">
            <div className="sd-label">Why Bestmark</div>
            <h2 className="sd-h2">Why Choose Us<br />For This Service</h2>
          </div>
          <div className="sd-why__right">
            <ul className="sd-why__list">
              {svc.why.map(w => (
                <li key={w} className="sd-why__item">
                  <CheckCircle size={16} color="#FF2B2B" style={{ flexShrink: 0 }} />
                  {w}
                </li>
              ))}
            </ul>
            <div className="sd-why__ratings">
              <div className="sd-stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="#FF2B2B" color="#FF2B2B" />
                ))}
              </div>
              <span>Rated 5 stars by 100+ Kenyan businesses</span>
            </div>
          </div>
        </div>
      </section>

      <section className="sd-cta">
        <div className="sd-cta__glow" />
        <div className="sd-cta__inner">
          <Award size={28} color="#FF2B2B" style={{ marginBottom: 16 }} />
          <h2 className="sd-cta__h2">Ready to get started<br />with {svc.label}?</h2>
          <p className="sd-cta__sub">
            Get a free, no-obligation quote in 24 hours.
            Call us, WhatsApp us, or fill in the contact form.
          </p>
          <div className="sd-cta__btns">
            <a href="tel:0794437498" className="sd-btn sd-btn--primary sd-btn--lg">
              <Phone size={16} /> Call 0794 437 498
            </a>
            <a href="mailto:info@bestmarkdesigner.com" className="sd-btn sd-btn--ghost sd-btn--lg">
              <Mail size={16} /> Email Us
            </a>
          </div>
          <div className="sd-cta__info">
            <span><Clock size={13} />Turnaround: {svc.turnaround}</span>
            <span><Zap size={13} />Starting from {svc.startingFrom}</span>
          </div>
        </div>
      </section>

      <SiblingNav current={svc.id} navigate={navigate} />

    </div>
  );
}